import Header from '../components/header'
import Footer from '../components/footer'
import ProfessionChart from '../components/charts/ProfessionChart'
import GenderCard from '../components/charts/GenderCard'
import EnglishLevelCard from '../components/charts/EnglishLevelCard'
import FrenchLevelCard from '../components/charts/FrenchLevelCard'
import EducationLevelCard from '../components/charts/EducationLevelCard'
import OriginChart from '../components/charts/OriginChart'
import ResidenceChart from '../components/charts/ResidenceChart'
import SpokenChart from '../components/charts/SpokenChart'
import {
  getCandidateStats,
  getOriginCountries,
  getResidenceCountries,
  getProfessionBroadCategoryCounts,
  getProfessionBroadCategory,
  getProfessionTitles,
  getEnglishLanguageLevels,
  getFrenchLanguageLevels,
  getEducationLevels,
  getTopSpokenLanguages,
} from '../utils/bigquery'
import { roundIt } from '../utils/functions'

import { useLoaderData } from '@remix-run/react'
import Resources from '../components/resources'
import Hiring from '../components/hiring'
import base from '../utils/airtable.server'
import { defer } from '@remix-run/node'
import { Await } from '@remix-run/react'
import { Suspense } from 'react'
import Loading from '../components/loading'
import { sendEmail } from '../utils/mailgun.server'
import { json } from '@remix-run/node'

export function meta() {
  return [
    { title: 'Talent - TalentLift Canada' },
    {
      name: 'description',
      content:
        'There is immense talent within refugee populations. We are a non-profit talent sourcing partner helping employers source and relocate talent from within refugee populations.',
    },
    {
      property: 'og:title',
      content: 'Talent - TalentLift Canada',
    },
    {
      property: 'og:description',
      content:
        'There is immense talent within refugee populations. We are a non-profit talent sourcing partner helping employers source and relocate talent from within refugee populations.',
    },
    {
      property: 'og:image',
      content: '/images/banners/talent_preview.jpg',
    },
    {
      property: 'og:url',
      content: 'https://www.talentlift.ca/talent',
    },
  ]
}

export const action = async ({ request }) => {
  const formData = await request.formData()
  const name = formData.get('name')
  const companyName = formData.get('companyName')
  const email = formData.get('email')
  const type = formData.get('type')
  const message = formData.get('message')

  // Check if any of the required fields are missing or empty
  if (!name || !companyName || !email || !message) {
    return json({ success: false, error: 'All fields are required.' })
  }

  try {
    const response = await sendEmail({ name, companyName, email, message, type })

    if (response.success) {
      return json({ success: true })
    } else {
      console.error('Email sending failed:', response.error)
      return json({ success: false, error: response.error })
    }
  } catch (error) {
    console.error('Error in action:', error)
    return json({ success: false, error: error.message })
  }
}

export const loader = async () => {
  const recordsPromise = base('resources')
    .select({
      sort: [{ field: 'date', direction: 'desc' }],
      filterByFormula: `{section} = 'employer'`,
    })
    .all()

  const resources = recordsPromise.then((records) =>
    records.map((record) => ({
      id: record.id,
      title: record.get('title'),
      link: record.get('link'),
      category: record.get('category'),
      sub_category: record.get('sub_category'),
      status: record.get('status'),
      section: record.get('section'),
      date: record.get('date'),
    }))
  )

  const professionData = Promise.all([
    getProfessionBroadCategory(),
    getProfessionBroadCategoryCounts(),
    getProfessionTitles(),
  ]).then(([broadCategory, broadCategoryCounts, titles]) => ({
    broadCategory,
    broadCategoryCounts,
    titles,
  }))

  return defer({
    candidateStats: getCandidateStats(),
    originCountries: getOriginCountries(),
    residenceCountries: getResidenceCountries(),
    professionData,
    englishLanguageLevels: getEnglishLanguageLevels(),
    frenchLanguageLevels: getFrenchLanguageLevels(),
    educationLevels: getEducationLevels(),
    spokenLanguages: getTopSpokenLanguages(),
    resources,
  })
}

export default function Index() {
  const {
    candidateStats,
    originCountries,
    residenceCountries,
    professionData,
    englishLanguageLevels,
    frenchLanguageLevels,
    educationLevels,
    spokenLanguages,
    resources,
  } = useLoaderData()

  return (
    <>
      <Header
        title="TALENT POOL"
        subtitle="With TalentLift, finding talent is transformative."
        backgroundimage="/images/banners/talent.jpg"
        ishomepage={false}
        shouldGetDarker={true}
        bgProp="bg-center"
      />
      <main>
        <div className="container mx-auto flex flex-col lg:flex-row justify-center items-center p-4 lg:px-24">
          <Suspense fallback={<Loading />}>
            <Await resolve={candidateStats} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(stats) => (
                <div className="w-full lg:w-1/3 bg-white lg:bg-transparent order-2 lg:order-1">
                  <p className="text-pointed text-display">Over {roundIt(stats[0].total_candidates)}</p>
                  <p className="text-title mb-8">registered professionals and growing.</p>
                  <p className="text-title">Candidates from</p>
                  <p className="text-pointed text-display">{stats[0].total_countries}</p>
                  <p className="text-title">countries</p>
                </div>
              )}
            </Await>
          </Suspense>
          <div className="w-full lg:w-3/4 bg-no-repeat bg-cover order-1 lg:order-2">
            <img src="/images/mapv2.png" alt="Map" className="object-contain max-w-full h-auto" />
          </div>
        </div>

        <div className="professions w-full mt-4 container mx-auto p-4 lg:px-24">
          <Suspense fallback={<Loading />}>
            <Await resolve={professionData} errorElement={<div>Failed to load. Please try again later.</div>}>
              {({ broadCategory, broadCategoryCounts, titles }) => (
                <ProfessionChart
                  broadcategory={broadCategory}
                  broadcategorycounts={broadCategoryCounts}
                  titles={titles}
                />
              )}
            </Await>
          </Suspense>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 tl-container justify-center">
          <Suspense fallback={<Loading />}>
            <Await resolve={candidateStats} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(stats) => (
                <GenderCard male={stats[0].gender_male} female={stats[0].gender_female} other={stats[0].gender_other} />
              )}
            </Await>
          </Suspense>
          <Suspense fallback={<Loading />}>
            <Await resolve={educationLevels} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(levels) => <EducationLevelCard levels={levels} />}
            </Await>
          </Suspense>
          <Suspense fallback={<Loading />}>
            <Await resolve={englishLanguageLevels} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(levels) => <EnglishLevelCard levels={levels} />}
            </Await>
          </Suspense>
          <Suspense fallback={<Loading />}>
            <Await resolve={frenchLanguageLevels} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(levels) => <FrenchLevelCard levels={levels} />}
            </Await>
          </Suspense>

          <Suspense fallback={<Loading />}>
            <Await resolve={originCountries} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(countries) => <OriginChart countriesData={countries} />}
            </Await>
          </Suspense>
          <Suspense fallback={<Loading />}>
            <Await resolve={residenceCountries} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(countries) => <ResidenceChart countriesData={countries} />}
            </Await>
          </Suspense>
          <Suspense fallback={<Loading />}>
            <Await resolve={spokenLanguages} errorElement={<div>Failed to load. Please try again later.</div>}>
              {(languages) => <SpokenChart language={languages} />}
            </Await>
          </Suspense>
        </div>

        <div className="tl-container pt-2">
          <p className="text-body">
            TalentLift is a grateful partner with refugee-serving organizations globally that work closely with the
            candidates we support. We partner with the United Nations High Commissioner for Refugees (UNHCR) and other
            trusted NGOs in the Middle East, East Africa, India, Latin America, and other regions.
          </p>
        </div>

        <Hiring />

        <div className="bg-grey5">
          <Resources initialCategory="general" resources={resources} color="bg-white" />
        </div>
        <Footer />
      </main>
    </>
  )
}
